// Global error capture — sends batched events to /api/errors/log.
//
// Captures:
//   • window.onerror (uncaught runtime exceptions)
//   • window.unhandledrejection (uncaught promise rejections)
//   • toast.error / toast.warning (wrapped in toastLogger.js)
//   • Failed XHR responses (axios interceptor in lib/api.js)
//
// Events are buffered in memory and flushed every 5s OR when 10 events queue
// up, whichever comes first. If the network is unavailable they're persisted
// to localStorage and replayed on next page load.
import { BACKEND_URL } from "@/lib/api";

const FLUSH_INTERVAL_MS = 5000;
const FLUSH_THRESHOLD = 10;
const STORAGE_KEY = "sc_error_buffer";
const VISITOR_KEY = "sc_visitor_id";
const SESSION_KEY = "sc_session_id";

let buffer = [];
let timer = null;
let installed = false;

function getVisitorId() {
  try { return localStorage.getItem(VISITOR_KEY); } catch { return null; }
}
function getSessionId() {
  try { return sessionStorage.getItem(SESSION_KEY); } catch { return null; }
}
function isAdminPage() {
  return typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
}

function makeEvent(partial) {
  return {
    severity: partial.severity || "error",
    source: partial.source || "client",
    message: String(partial.message ?? "(no message)"),
    stack: partial.stack || null,
    page: typeof window !== "undefined" ? window.location.pathname : null,
    url: typeof window !== "undefined" ? window.location.href : null,
    http_method: partial.http_method || null,
    http_status: partial.http_status ?? null,
    endpoint: partial.endpoint || null,
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    is_admin: isAdminPage(),
    extra: partial.extra || null,
    client_timestamp: new Date().toISOString(),
  };
}

function scheduleFlush() {
  if (timer) return;
  timer = setTimeout(flush, FLUSH_INTERVAL_MS);
}

async function flush() {
  if (timer) { clearTimeout(timer); timer = null; }
  if (!buffer.length) return;
  const events = buffer.splice(0, buffer.length);
  try {
    const res = await fetch(`${BACKEND_URL}/api/errors/log`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // keepalive lets the request survive a page unload
      keepalive: true,
      body: JSON.stringify({ events }),
    });
    if (!res.ok) throw new Error(`status ${res.status}`);
  } catch (e) {
    // Network unavailable — persist for next page load
    try {
      const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...existing, ...events].slice(-100)));
    } catch {
      /* localStorage full or unavailable — give up */
    }
  }
}

// Public API: enqueue a single event
export function logError(event) {
  buffer.push(makeEvent(event));
  if (buffer.length >= FLUSH_THRESHOLD) {
    flush();
  } else {
    scheduleFlush();
  }
}

// Replay any events persisted from a previous load when offline
async function replayPersisted() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const events = JSON.parse(raw);
    if (!Array.isArray(events) || !events.length) return;
    localStorage.removeItem(STORAGE_KEY);
    buffer.push(...events);
    flush();
  } catch {
    /* swallow */
  }
}

export function installErrorCapture() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // 1. window.onerror — synchronous runtime errors
  window.addEventListener("error", (event) => {
    // Resource load errors (img/script 404s) have no `.error` and a target
    // node — skip those to keep the feed signal-heavy.
    if (!event.error && event.target && event.target !== window) return;
    logError({
      severity: "error",
      source: "client",
      message: event.message || (event.error && event.error.message) || "Unhandled error",
      stack: event.error?.stack || null,
    });
  });

  // 2. Unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    let message;
    let stack = null;
    if (reason instanceof Error) {
      message = reason.message;
      stack = reason.stack;
    } else if (typeof reason === "string") {
      message = reason;
    } else {
      try { message = JSON.stringify(reason); } catch { message = String(reason); }
    }
    logError({
      severity: "error",
      source: "promise",
      message: message || "Unhandled promise rejection",
      stack,
    });
  });

  // 3. Flush on page hide
  window.addEventListener("pagehide", () => { flush(); });
  window.addEventListener("beforeunload", () => { flush(); });

  // 4. Replay any leftover events from a previous offline session
  replayPersisted();
}
